import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import queueApi from '../services/api';
import signalRService from '../services/signalR';
import { useLanguage } from '../contexts/LanguageContext';
import './AppCodeEntry.css';

import phoneQrImage from '../images/Rectangle.png';
import farmoishLogo from '../images/icon.svg';
import Spinner from './Spinner';

const AppCodeEntry = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getText } = useLanguage();
  const service = location.state?.service;

  if (!service) {
    console.error("No service provided, redirecting to services page");
    navigate("/");
    return null;
  }

  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [countdown, setCountdown] = useState(25);
  const [lastInteraction, setLastInteraction] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (loading) return;

    const timer = setInterval(() => {
      const now = Date.now();
      const timeSinceLastInteraction = (now - lastInteraction) / 1000;

      if (timeSinceLastInteraction >= 25) {
        clearInterval(timer);
        navigate('/');
        return;
      }

      const remaining = Math.max(0, 25 - Math.floor(timeSinceLastInteraction));
      setCountdown(remaining);
    }, 1000);

    return () => clearInterval(timer);
  }, [lastInteraction, loading, navigate]);

  const isComplete = code.length === 5;

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (loading) return;

      const key = event.key;

      if (key >= '0' && key <= '9') {
        if (code.length < 5) {
          setCode(prev => prev + key);
          setError(false);
          setLastInteraction(Date.now());
          setCountdown(25);
        }
      } else if (key === 'Backspace' || key === 'Delete') {
        setCode(prev => prev.slice(0, -1));
        setError(false);
        setLastInteraction(Date.now());
        setCountdown(25);
      } else if (key === 'Enter' && code.length === 5) {
        handleSubmit();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [loading, code]);

  const handleNumpadClick = (num) => {
    if (code.length < 5) {
      setCode(prev => prev + num.toString());
      setError(false);
      setLastInteraction(Date.now());
      setCountdown(25);
    }
  };

  const handleDelete = () => {
    setCode(prev => prev.slice(0, -1));
    setError(false);
    setLastInteraction(Date.now());
    setCountdown(25);
  };

  const handleSubmit = async () => {
    if (isComplete) {
      if (!service?.terminalServiceId) {
        console.error('No terminalServiceId provided');
        setError(true);
        return;
      }

      setLoading(true);
      setError(false);
      try {
        const response = await queueApi.submitAppCode(code, service.terminalServiceId);

        if (response && response.isExisting) {
          signalRService.refreshQueue();
          navigate('/already', { state: { code, queueData: response.data } });
          return;
        }

        if (response) {
          signalRService.refreshQueue();
          navigate('/create', { state: { queueData: response } });
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || "";
        if (errorMessage.includes("not found")) {
          setError(true);
        } else if (errorMessage.includes("no products")) {
          navigate('/product-not-found', { state: { code } });
        } else {
          setError(true);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const renderCodeInputs = () => {
    const inputs = [];
    for (let i = 0; i < 5; i++) {
      const char = code[i];
      const isActive = i === code.length && !error;
      const isFilled = i < code.length;

      inputs.push(
        <div
          key={i}
          className={`code-digit-box ${isFilled ? 'filled' : ''} ${isActive ? 'active' : ''} ${error ? 'error-border' : ''}`}
        >
          {isFilled ? char : null}
          {isActive && <div className="cursor-line"></div>}
        </div>
      );
    }
    return inputs;
  };

  return (
    <div className="app-code-page">
      {loading && <Spinner />}
      <main className="app-main-content">
        <div className="left-section">
          <h2 className="section-title">
            {getText('Введите код из приложения', 'Кодро аз барнома ворид кунед')}
          </h2>

          <div className="code-input-row">
            {renderCodeInputs()}
          </div>

          {!error ? (
            <p className="timer-text">
              {getText('Код действителен в течение 5 минут', 'Код дар давоми 5 дақиқа эътибор дорад')}
            </p>
          ) : (
            <div className="error-msg-container">
              <div className="error-badge">
                <span className="error-icon">!</span>
                <span className="error-title">{getText('Неверный код!', 'Коди нодуруст!')}</span>
              </div>
              <p className="error-hint">
                {getText('Убедитесь, что код актуален и введен правильно', 'Боварӣ ҳосил кунед, ки код дуруст аст')}
              </p>
            </div>
          )}

          <div className="qr-instruction-card">
            <div className="phone-mockup">
              <img src={phoneQrImage} alt="App QR" />
            </div>
            <div className="qr-text">
              <div className="mini-logo">
                <img src={farmoishLogo} alt="logo" />
              </div>
              <p>
                {getText(
                  'Откройте приложение ',
                  'Барномаи '
                )}
                <strong className="farmoish-text">Farmoish</strong>
                {getText(
                  ', перейдите в раздел QR-кода и введите отображаемые цифры ниже',
                  '-ро кушоед, ба бахши QR-код гузаред ва рақамҳои нишондодашударо ворид кунед'
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="right-section">
          <div className="numpad-container">
            <div className="numpad-grid-modern">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                <button key={num} className="num-btn" onClick={() => handleNumpadClick(num)} disabled={loading}>
                  {num}
                </button>
              ))}
              <div className="empty-space" />
              <button className="num-btn" onClick={() => handleNumpadClick(0)} disabled={loading}>0</button>
              <button className="num-btn delete-btn" onClick={handleDelete} disabled={loading}>
                <svg width="41" height="31" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                  <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
                  <line x1="18" y1="9" x2="12" y2="15" />
                  <line x1="12" y1="9" x2="18" y2="15" />
                </svg>
              </button>
            </div>
          </div>

          <button
            className={`main-submit-btn ${isComplete ? 'ready' : 'disabled'}`}
            disabled={!isComplete || loading}
            onClick={handleSubmit}
          >
            {loading ? (
              <span className="spinner"></span>
            ) : getText('Получить очередь', 'Гирифтани навбат')}
          </button>
        </div>
      </main>
    </div>
  );
};

export default AppCodeEntry;